import { useState } from 'react'
import { Mail } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../lib/shadcn/dialog'
import { Button } from '../../lib/shadcn/button'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { useNotifyDriverKm } from '../../hooks/backend/shifts'
import { formatDate, formatNum } from '../../utils/interviews'

// The exact, verbatim reassurance sentence that must appear on the confirm
// step. It is deliberately non-accusatory and states that no deduction is made.
const CONFIRM_SENTENCE =
  'This queues an email asking the driver to check their readings. It does not accuse them and it makes no deduction.'

export function NotifyDriverDialog({
  shiftId,
  driverName,
  shiftDate,
  declaredKm,
  cartrackKm,
  onNotified,
}: {
  shiftId: string
  driverName: string | null
  shiftDate: string | null
  declaredKm: number | string | null
  cartrackKm: number | string | null
  // Called after the email is queued so the caller can refresh the queue.
  onNotified: () => void
}) {
  const [open, setOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { trigger, loading } = useNotifyDriverKm()

  async function handleSend() {
    setError(null)
    try {
      await trigger({ shift_id: shiftId }, { skipCache: true }).result
      toast.success('Email queued for the driver.')
      setOpen(false)
      onNotified()
    } catch (err) {
      // Surface the DB function's refusal message verbatim — including
      // "no email address on file" and "already been written to".
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) setError(null)
        setOpen(next)
      }}
    >
      <Button
        variant="outline"
        size="sm"
        className="h-7 gap-1.5 px-2 text-xs"
        onClick={() => {
          setError(null)
          setOpen(true)
        }}
      >
        <Mail className="h-3.5 w-3.5" />
        Notify driver
      </Button>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Notify driver</DialogTitle>
          <DialogDescription>{CONFIRM_SENTENCE}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="rounded-md border border-border bg-muted/40 p-3 text-sm">
            <div className="flex items-center justify-between gap-3">
              <span className="text-muted-foreground">Driver</span>
              <span className="font-medium text-foreground">{driverName || 'Unnamed driver'}</span>
            </div>
            <div className="mt-1.5 flex items-center justify-between gap-3">
              <span className="text-muted-foreground">Shift date</span>
              <span className="font-medium text-foreground">{formatDate(shiftDate)}</span>
            </div>
            <div className="mt-1.5 flex items-center justify-between gap-3">
              <span className="text-muted-foreground">Declared km</span>
              <span className="font-medium tabular-nums text-foreground">
                {formatNum(declaredKm, 0)}
              </span>
            </div>
            <div className="mt-1.5 flex items-center justify-between gap-3">
              <span className="text-muted-foreground">Cartrack km</span>
              <span className="font-medium tabular-nums text-foreground">
                {formatNum(cartrackKm, 0)}
              </span>
            </div>
          </div>

          {error && (
            <p className="rounded-md border border-destructive/30 bg-destructive/10 p-2.5 text-sm text-destructive">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={loading}>
            {loading && <Spinner className="mr-1.5 h-4 w-4" />}
            <Mail className="mr-1.5 h-4 w-4" />
            Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
